import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'
import globalStyles from '../styles/global.module.css'
import styles from './UpdateProgress.module.css'
import apiConfig from '../config/api'

const UpdateProgress = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [hikeData, setHikeData] = useState({
    mile: 0,
    text: '',
    title: '',
    location_coordinates: null,
    time: new Date().toISOString()
  })
  const [photos, setPhotos] = useState([])
  const [message, setMessage] = useState('')
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Format date string to local PDT datetime-local input value
  const formatDateForInput = (isoString) => {
    const date = new Date(isoString)
    // Convert to PDT by adjusting for timezone offset
    const pdtDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
    return pdtDate.toISOString().split('.')[0]
  }

  // Resize an image and return as a File object
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      // Create image element to load the file
      const img = new Image()
      img.src = URL.createObjectURL(file)
      
      img.onload = () => {
        // Get target dimensions (max width/height 1600px)
        const MAX_SIZE = 4032
        let width = img.width
        let height = img.height
        
        // Calculate new dimensions if image is larger than MAX_SIZE
        if (width > MAX_SIZE || height > MAX_SIZE) {
          if (width > height) {
            height = Math.round(height * (MAX_SIZE / width))
            width = MAX_SIZE
          } else {
            width = Math.round(width * (MAX_SIZE / height))
            height = MAX_SIZE
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        // Convert to file
        canvas.toBlob((blob) => {
          const resizedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve(resizedFile)
        }, 'image/jpeg', 0.8) // Quality 0.8 is a good balance
      }
    })
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    
    try {
      setIsUploading(true)
      
      // Create FormData to handle file uploads
      const formData = new FormData()
      
      // Log hikeData before submission
      console.log('Submitting hikeData:', hikeData)
      console.log('Location coordinates:', hikeData.location_coordinates)
      
      // Add all hikeData fields to formData
      Object.keys(hikeData).forEach(key => {
        if (key === 'location_coordinates' && hikeData[key]) {
          const coordinatesString = JSON.stringify(hikeData[key])
          console.log('Stringified coordinates:', coordinatesString)
          formData.append(key, coordinatesString)
        } else {
          formData.append(key, hikeData[key])
        }
      })
      
      // Resize and add each photo to FormData
      const resizedPhotos = await Promise.all(
        photos.map(photo => resizeImage(photo))
      )
      
      // Add resized photos to form data
      resizedPhotos.forEach(photo => {
        formData.append('photos', photo)
      })

      // Log form data entries for debugging
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`)
      }
      
      const response = await axios.post(`${apiConfig.baseUrl}${apiConfig.endpoints.updates}`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      console.log('Server response:', response.data)
      setMessage('Progress updated successfully!')
      setIsUploading(false)
      
      // Redirect to admin page after successful update
      setTimeout(() => {
        navigate('/admin')
      }, 1500)
    } catch (error) {
      setIsUploading(false)
      setMessage('Error updating progress. Please try again.')
      console.error('Update error:', error.response?.data || error.message || error)
    }
  }

  const handleFileChange = (e) => {
    const fileList = Array.from(e.target.files)
    setPhotos(fileList)
  }

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMessage('Geolocation is not supported by your browser')
      return
    }

    setIsGettingLocation(true)
    setMessage('')
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords
          console.log('Got coordinates from browser:', latitude, longitude)
          
          // Create properly formatted coordinates object
          const locationCoords = { 
            lat: latitude, 
            lng: longitude 
          }
          
          console.log('Formatted coordinates object:', locationCoords)
          
          // Update state with the coordinates
          setHikeData(prevData => {
            // Make sure to initialize location_coordinates if it doesn't exist
            const updatedData = {
              ...prevData, 
              location_coordinates: locationCoords
            }
            console.log('Updated hikeData with coordinates:', updatedData)
            return updatedData
          })
        } catch (error) {
          console.error('Error processing location:', error)
          setMessage('Error getting location. Please try again.')
          setIsGettingLocation(false)
        }
        setIsGettingLocation(false)
      },
      (error) => {
        console.error('Geolocation error:', error)
        setMessage(`Location error: ${error.message}`)
        setIsGettingLocation(false)
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000, 
        maximumAge: 0 
      }
    )
  }

  // Add a label to indicate timezone
  const formatDateLabel = () => {
    return `Date and Time (PDT)`
  }

  return (
    <Layout>
      <div className={globalStyles.card}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>Update Hiking Progress</h2>
          <button 
            onClick={() => navigate('/admin')} 
            className={styles.backButton}
          >
            Back to Dashboard
          </button>
        </div>
        
        {message && (
          <p className={`${styles.statusMessage} ${message.includes('Error') ? styles.errorMessage : styles.successMessage}`}>
            {message}
          </p>
        )}
        
        <form onSubmit={handleUpdate}>
          <div className={globalStyles.formGroup}>
            <label htmlFor="mile">Current mileage</label>
            <input
              type="number"
              id="mile"
              className={globalStyles.input}
              value={hikeData.mile}
              onChange={(e) => setHikeData({...hikeData, mile: Number(e.target.value)})}
              required
            />
          </div>
          
          <div className={globalStyles.formGroup}>
            <label htmlFor="title">Title (optional)</label>
            <input
              type="text"
              id="title"
              className={globalStyles.input}
              value={hikeData.title}
              onChange={(e) => setHikeData({...hikeData, title: e.target.value})}
            />
          </div>
          
          <div className={globalStyles.formGroup}>
            <label htmlFor="text">Update Text</label>
            <textarea
              id="text"
              className={globalStyles.input}
              value={hikeData.text}
              onChange={(e) => setHikeData({...hikeData, text: e.target.value})}
              required
              rows="4"
            />
          </div>
          
          {/* Coordinates Input Fields */}
          <div className={globalStyles.formGroup}>
            <label>Coordinates</label>
            <div className={styles.coordinatesInputGroup}>
              <div className={styles.coordinateField}>
                <label htmlFor="latitude">Latitude</label>
                <input
                  type="number"
                  id="latitude"
                  step="0.0000001"
                  className={globalStyles.input}
                  value={hikeData.location_coordinates?.lat || ''}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    setHikeData({
                      ...hikeData, 
                      location_coordinates: {
                        ...hikeData.location_coordinates,
                        lat
                      }
                    });
                  }}
                  placeholder="Latitude (e.g., 37.7749)"
                />
              </div>
              <div className={styles.coordinateField}>
                <label htmlFor="longitude">Longitude</label>
                <input
                  type="number"
                  id="longitude"
                  step="0.0000001"
                  className={globalStyles.input}
                  value={hikeData.location_coordinates?.lng || ''}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    setHikeData({
                      ...hikeData, 
                      location_coordinates: {
                        ...hikeData.location_coordinates,
                        lng
                      }
                    });
                  }}
                  placeholder="Longitude (e.g., -122.4194)"
                />
              </div>
            </div>
            <button 
              type="button" 
              onClick={getCurrentLocation} 
              className={styles.locationButton}
              disabled={isGettingLocation}
            >
              {isGettingLocation ? 'Getting...' : 'Use Device Location'}
            </button>
          </div>
          
          <div className={globalStyles.formGroup}>
            <label htmlFor="time">{formatDateLabel()}</label>
            <input
              type="datetime-local"
              id="time"
              className={globalStyles.input}
              value={formatDateForInput(hikeData.time)}
              onChange={(e) => {
                // Convert the local datetime to ISO
                const date = new Date(e.target.value)
                setHikeData({...hikeData, time: date.toISOString()})
              }}
              required
            />
          </div>
          
          <div className={globalStyles.formGroup}>
            <label htmlFor="photos">Photos</label>
            <input
              type="file"
              id="photos"
              className={globalStyles.input}
              onChange={handleFileChange}
              multiple
              accept="image/*"
            />
            {photos.length > 0 && (
              <div className={styles.photoPreview}>
                <p>{photos.length} photo{photos.length > 1 ? 's' : ''} selected</p>
                <ul className={styles.photoList}>
                  {photos.map((photo, index) => (
                    <li key={index} className={styles.photoItem}>
                      {photo.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            className={`${globalStyles.button} ${styles.submitButton}`}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Update Progress'}
          </button>
        </form>
      </div>
    </Layout>
  )
}

export default UpdateProgress 